import ReactGa from "react-ga4";

const ANALYTICS_SESSION_KEY = "bellhopt_analytics_session";
const RECOMMENDATION_ATTRIBUTION_KEY = "bellhopt_recommendation_attribution";
const CHECKOUT_SNAPSHOT_KEY = "bellhopt_checkout_analytics";
const MAX_ATTRIBUTIONS = 20;
const MAX_TEXT_LENGTH = 100;
export const GROCERY_EXPERIENCE_VERSION = "redesign_v1";

const BLOCKED_KEY_PATTERN =
  /(address|email|phone|contact|guest|reservation|confirmation|cart_?code|token|secret|password|payment|card|dob|birth|arrival|note|latitude|longitude|transaction_id_raw)/i;
const SAFE_ITEM_KEYS = new Set([
  "item_id",
  "item_name",
  "item_brand",
  "item_category",
  "item_list_id",
  "item_list_name",
  "index",
  "price",
  "quantity",
  "affiliation",
  "coupon",
  "discount",
]);
const SAFE_ATTRIBUTION_KEYS = new Set([
  "product_id",
  "source",
  "placement",
  "category",
  "recommendation_id",
  "grocery_experience_version",
  "timestamp",
]);

let initialized = false;
let lastPageView = { path: null, timestamp: 0 };

const canUseWindow = () => typeof window !== "undefined";

const safeStorage = (storageName) => {
  if (!canUseWindow()) return null;
  try {
    return window[storageName];
  } catch {
    return null;
  }
};

const createAnonymousId = () => {
  if (canUseWindow() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  if (canUseWindow() && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getAnonymousSessionId = () => {
  const storage = safeStorage("sessionStorage");
  if (!storage) return createAnonymousId();
  try {
    const existing = storage.getItem(ANALYTICS_SESSION_KEY);
    if (existing && /^[a-zA-Z0-9-]{12,64}$/.test(existing)) return existing;
    const next = createAnonymousId();
    storage.setItem(ANALYTICS_SESSION_KEY, next);
    return next;
  } catch {
    return createAnonymousId();
  }
};

const safeText = (value, maxLength = MAX_TEXT_LENGTH) => {
  if (typeof value !== "string") return null;
  const text = value.trim().slice(0, maxLength);
  if (!text) return null;
  if (/@/.test(text)) return null;
  if (/\b(?:\d[ -]*?){12,19}\b/.test(text)) return null;
  if (/\b(?:\+?1[ -.]?)?\(?\d{3}\)?[ -.]?\d{3}[ -.]?\d{4}\b/.test(text)) return null;
  return text;
};

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const sanitizeItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const sanitized = {};
  SAFE_ITEM_KEYS.forEach((key) => {
    const value = item[key];
    if (typeof value === "number") {
      if (Number.isFinite(value)) sanitized[key] = value;
      return;
    }
    const text = safeText(value);
    if (text !== null) sanitized[key] = text;
  });
  return Object.keys(sanitized).length ? sanitized : null;
};

const sanitizeParams = (params = {}) => {
  const sanitized = {
    analytics_session_id: getAnonymousSessionId(),
    grocery_experience_version: GROCERY_EXPERIENCE_VERSION,
  };
  Object.entries(params).forEach(([key, value]) => {
    if (BLOCKED_KEY_PATTERN.test(key) || value == null) return;
    if (key === "items" && Array.isArray(value)) {
      sanitized.items = value.slice(0, 100).map(sanitizeItem).filter(Boolean);
      return;
    }
    if (typeof value === "boolean") {
      sanitized[key] = value;
      return;
    }
    if (typeof value === "number") {
      if (Number.isFinite(value)) sanitized[key] = value;
      return;
    }
    const text = safeText(value);
    if (text !== null) sanitized[key] = text;
  });
  return sanitized;
};

export const initializeAnalytics = () => {
  if (initialized || ReactGa.isInitialized) {
    initialized = true;
    return true;
  }
  const trackingId = process.env.REACT_APP_GA_TRACKING_ID;
  if (!trackingId) return false;
  try {
    ReactGa.initialize(trackingId, {
      gtagOptions: { send_page_view: false, anonymize_ip: true },
    });
    initialized = true;
    return true;
  } catch {
    return false;
  }
};

export const sendAnalyticsEvent = (eventName, params = {}) => {
  const safeName = safeText(eventName, 40);
  if (!safeName || !initializeAnalytics()) return false;
  try {
    ReactGa.event(safeName, sanitizeParams(params));
    return true;
  } catch {
    return false;
  }
};

export const trackPageView = (pathname) => {
  if (!initializeAnalytics()) return false;
  const rawPath =
    typeof pathname === "string" && pathname.startsWith("/")
      ? pathname.split(/[?#]/)[0]
      : "/";
  const path = rawPath
    .replace(/^\/guest\/r\/[^/]+/, "/guest/r/:reservation")
    .replace(/^\/guest\/[^/]+\/savedcart\/[^/]+/, "/guest/:property/savedcart/:cart")
    .replace(/^\/guest\/[^/]+\/addon\/[^/]+/, "/guest/:property/addon/:order")
    .replace(/^\/guest\/(?!r(?:\/|$))[^/]+$/, "/guest/:property")
    .replace(/^\/order-tracking\/[^/]+/, "/order-tracking/:order")
    .replace(/^\/order\/[^/]+/, "/order/:order")
    .replace(/^\/package\/[^/]+/, "/package/:package");
  const now = Date.now();
  if (lastPageView.path === path && now - lastPageView.timestamp < 1000) return false;
  lastPageView = { path, timestamp: now };
  try {
    ReactGa.send({ hitType: "pageview", page: path });
    return true;
  } catch {
    return false;
  }
};

const resolveItemPrice = (item) => {
  const candidates = [
    item?.price,
    item?.items?.[0]?.price?.regular,
    item?.items?.[0]?.price?.promo,
    item?.price?.regular,
  ];
  for (const candidate of candidates) {
    const price = safeNumber(candidate);
    if (price !== null && price >= 0) return price;
  }
  return 0;
};

export const toAnalyticsItem = (item, overrides = {}) =>
  sanitizeItem({
    item_id: overrides.item_id || item?.productId || item?.upc || item?._id,
    item_name: overrides.item_name || item?.description || item?.name || item?.title,
    item_brand: overrides.item_brand || item?.brand,
    item_category:
      overrides.item_category ||
      item?._categoryName ||
      (Array.isArray(item?.categories) ? item.categories[0] : item?.category),
    item_list_id: overrides.item_list_id,
    item_list_name: overrides.item_list_name,
    index: overrides.index,
    price: overrides.price ?? resolveItemPrice(item),
    quantity: overrides.quantity ?? Number(item?.quantity || 1),
  });

const ecommerceEvent = (name, { items = [], value, currency = "USD", ...params } = {}) =>
  sendAnalyticsEvent(name, {
    ...params,
    currency,
    value: safeNumber(value) ?? undefined,
    items: items.map((item) => toAnalyticsItem(item) || item).filter(Boolean),
  });

export const trackSearch = ({ source = "navigation", term } = {}) =>
  sendAnalyticsEvent("search", {
    search_term: "product_search",
    search_source: source,
    search_term_length: typeof term === "string" ? Math.min(term.trim().length, 100) : 0,
  });

export const trackLiveSearchTakeover = ({ termLength = 0 } = {}) =>
  sendAnalyticsEvent("live_search_takeover", {
    search_term: "product_search",
    search_source: "live",
    search_term_length: Math.max(
      0,
      Math.min(Number(termLength) || 0, 100)
    ),
  });

export const trackGroceryHeroState = ({
  state = "empty_cart",
  itemCount = 0,
  hasSubtotal = false,
} = {}) =>
  sendAnalyticsEvent("grocery_hero_state_view", {
    hero_state: state,
    cart_item_count: Math.max(0, Number(itemCount) || 0),
    has_subtotal: Boolean(hasSubtotal),
  });

export const trackRecommendationSlotEvent = ({
  action,
  placement,
  slot,
  modelVersion,
  requestId,
} = {}) =>
  sendAnalyticsEvent(`recommendation_slot_${action || "updated"}`, {
    recommendation_placement: placement,
    recommendation_slot: Number(slot) || 0,
    recommendation_model_version: modelVersion,
    recommendation_request_id: requestId,
  });

export const trackCategoryDisclosure = ({
  action,
  category,
  visibleCount,
  total,
  resumedPosition = false,
} = {}) =>
  sendAnalyticsEvent(`category_${action || "updated"}`, {
    category_name: category,
    visible_product_count: Math.max(0, Number(visibleCount) || 0),
    total_product_count: Math.max(0, Number(total) || 0),
    resumed_position: Boolean(resumedPosition),
  });

export const trackViewItem = (item, params = {}) =>
  ecommerceEvent("view_item", { ...params, items: [item] });

export const trackAddToCart = (item, params = {}) =>
  ecommerceEvent("add_to_cart", { ...params, items: [item] });

export const trackRemoveFromCart = (item, params = {}) =>
  ecommerceEvent("remove_from_cart", { ...params, items: [item] });

export const trackViewCart = (items, params = {}) =>
  ecommerceEvent("view_cart", { ...params, items });

export const trackBeginCheckout = (items, params = {}) =>
  ecommerceEvent("begin_checkout", { ...params, items });

export const trackCheckoutValidationFailure = ({ sections = [], errorCount = 1 } = {}) =>
  sendAnalyticsEvent("checkout_validation_failed", {
    invalid_sections: sections.slice(0, 8).map((section) => safeText(section, 30)).filter(Boolean).join(","),
    error_count: errorCount,
  });

export const trackCheckoutStartFailure = ({ failureType = "unknown", status } = {}) =>
  sendAnalyticsEvent("checkout_start_failed", {
    failure_type: failureType,
    http_status: safeNumber(status) ?? undefined,
  });

export const storeRecommendationAttribution = (attribution) => {
  if (!attribution || typeof attribution !== "object") return;
  const storage = safeStorage("sessionStorage");
  if (!storage) return;
  const sanitized = {};
  sanitized.grocery_experience_version = GROCERY_EXPERIENCE_VERSION;
  SAFE_ATTRIBUTION_KEYS.forEach((key) => {
    if (key === "timestamp") {
      sanitized.timestamp = safeNumber(attribution.timestamp) || Date.now();
      return;
    }
    const text = safeText(attribution[key], 80);
    if (text !== null) sanitized[key] = text;
  });
  if (!sanitized.product_id || !sanitized.source) return;
  try {
    const parsed = JSON.parse(storage.getItem(RECOMMENDATION_ATTRIBUTION_KEY) || "[]");
    const current = Array.isArray(parsed) ? parsed : [];
    const next = [
      sanitized,
      ...current.filter((entry) => entry?.product_id !== sanitized.product_id),
    ].slice(0, MAX_ATTRIBUTIONS);
    storage.setItem(RECOMMENDATION_ATTRIBUTION_KEY, JSON.stringify(next));
  } catch {
    storage.setItem(RECOMMENDATION_ATTRIBUTION_KEY, JSON.stringify([sanitized]));
  }
};

export const getRecommendationAttribution = (productId) => {
  const storage = safeStorage("sessionStorage");
  const safeProductId = safeText(productId, 80);
  if (!storage || !safeProductId) return null;
  try {
    const parsed = JSON.parse(storage.getItem(RECOMMENDATION_ATTRIBUTION_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed.find((entry) => entry?.product_id === safeProductId) || null
      : null;
  } catch {
    return null;
  }
};

export const storeCheckoutAnalyticsSnapshot = ({ items = [], value, currency = "USD" } = {}) => {
  const storage = safeStorage("sessionStorage");
  if (!storage) return null;
  const purchaseId = createAnonymousId();
  const snapshot = {
    purchase_id: purchaseId,
    currency,
    value: safeNumber(value) || 0,
    items: items.slice(0, 100).map(toAnalyticsItem).filter(Boolean),
    grocery_experience_version: GROCERY_EXPERIENCE_VERSION,
    timestamp: Date.now(),
  };
  try {
    storage.setItem(CHECKOUT_SNAPSHOT_KEY, JSON.stringify(snapshot));
    return snapshot;
  } catch {
    return null;
  }
};

export const consumeCheckoutAnalyticsSnapshot = () => {
  const storage = safeStorage("sessionStorage");
  if (!storage) return null;
  try {
    const snapshot = JSON.parse(storage.getItem(CHECKOUT_SNAPSHOT_KEY) || "null");
    storage.removeItem(CHECKOUT_SNAPSHOT_KEY);
    if (!snapshot || Date.now() - Number(snapshot.timestamp) > 24 * 60 * 60 * 1000) return null;
    return {
      purchase_id: safeText(snapshot.purchase_id, 64),
      currency: safeText(snapshot.currency, 3) || "USD",
      value: safeNumber(snapshot.value) || 0,
      items: Array.isArray(snapshot.items)
        ? snapshot.items.slice(0, 100).map(sanitizeItem).filter(Boolean)
        : [],
      grocery_experience_version:
        safeText(snapshot.grocery_experience_version, 30) ||
        GROCERY_EXPERIENCE_VERSION,
    };
  } catch {
    storage.removeItem(CHECKOUT_SNAPSHOT_KEY);
    return null;
  }
};

export const trackPurchase = ({ purchaseId, items = [], value, currency = "USD" } = {}) =>
  ecommerceEvent("purchase", {
    transaction_id: purchaseId,
    items,
    value,
    currency,
  });
